import type { BootstrapConfig } from 'posthog-js';

/** Query keys the directory site may append when sending users here (same PostHog project). */
const DISTINCT_KEYS = [
  'ph_distinct_id',
  'posthog_distinct_id',
  'distinct_id',
] as const;
const SESSION_KEYS = [
  'ph_session_id',
  'posthog_session_id',
  'session_id',
] as const;
const IDENTIFIED_KEYS = ['ph_is_identified', 'is_identified'] as const;

const ALL_PASS_THROUGH = [
  ...DISTINCT_KEYS,
  ...SESSION_KEYS,
  ...IDENTIFIED_KEYS,
] as const;

const MAX_DISTINCT_LEN = 400;

function mergedSearchParams(): URLSearchParams {
  const merged = new URLSearchParams(window.location.search);
  const hash = window.location.hash;
  const q = hash.indexOf('?');
  if (q === -1) return merged;
  const fromHash = new URLSearchParams(hash.slice(q + 1));
  fromHash.forEach((value, key) => {
    if (!merged.has(key)) merged.set(key, value);
  });
  return merged;
}

function firstParam(
  params: URLSearchParams,
  keys: readonly string[]
): string | undefined {
  for (const key of keys) {
    const v = params.get(key)?.trim();
    if (v) return v;
  }
  return undefined;
}

/** PostHog session IDs are UUID v7; reject malformed values so init stays stable. */
function isLikelySessionIdV7(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    id
  );
}

function parseIsIdentified(params: URLSearchParams): boolean {
  for (const key of IDENTIFIED_KEYS) {
    const v = params.get(key)?.trim().toLowerCase();
    if (v === '1' || v === 'true' || v === 'yes') return true;
  }
  return false;
}

/**
 * Read PostHog bootstrap values from the URL (search and hash query) so sessions
 * can continue across domains when the first site appends distinct + session ids.
 *
 * On codec422 (or any referrer), before navigation:
 *   link.searchParams.set('ph_distinct_id', posthog.get_distinct_id());
 *   link.searchParams.set('ph_session_id', posthog.get_session_id());
 * If the user is identified there, add: ph_is_identified=1
 * Use the same project token and api_host on both sites.
 */
export function getPosthogBootstrapFromUrl(): BootstrapConfig | undefined {
  const params = mergedSearchParams();
  let distinctID = firstParam(params, DISTINCT_KEYS);
  if (distinctID && distinctID.length > MAX_DISTINCT_LEN) distinctID = undefined;

  const sessionRaw = firstParam(params, SESSION_KEYS);
  const sessionID =
    sessionRaw && isLikelySessionIdV7(sessionRaw) ? sessionRaw : undefined;

  const isIdentifiedID = parseIsIdentified(params) && Boolean(distinctID);

  const bootstrap: BootstrapConfig = {};
  if (distinctID) bootstrap.distinctID = distinctID;
  if (sessionID) bootstrap.sessionID = sessionID;
  if (isIdentifiedID) bootstrap.isIdentifiedID = true;

  return Object.keys(bootstrap).length ? bootstrap : undefined;
}

/** Remove pass-through params from the address bar after bootstrap is applied. */
export function stripPosthogPassThroughParamsFromUrl(): void {
  const url = new URL(window.location.href);
  let changed = false;
  for (const key of ALL_PASS_THROUGH) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }

  const hash = url.hash;
  const qi = hash.indexOf('?');
  if (qi !== -1) {
    const base = hash.slice(0, qi);
    const hp = new URLSearchParams(hash.slice(qi + 1));
    let hashChanged = false;
    for (const key of ALL_PASS_THROUGH) {
      if (hp.has(key)) {
        hp.delete(key);
        hashChanged = true;
      }
    }
    if (hashChanged) {
      const qs = hp.toString();
      url.hash = qs ? `${base}?${qs}` : base;
      changed = true;
    }
  }

  if (changed) {
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  }
}
